import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';
import { useCompany } from '../contexts/CompanyContext';
import PaymentForm from './PaymentForm';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  openJobs: number | 'unlimited';
  description: string;
  isBestValue?: boolean;
}

const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 99,
    openJobs: 50,
    description: 'Perfect for small teams',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 249,
    openJobs: 'unlimited',
    description: 'For large operations',
    isBestValue: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 199,
    openJobs: 100,
    description: 'Ideal for growing businesses',
  },
];

interface SubscriptionSelectModalProps {
  open: boolean;
  onPlanSelected: (planId: string) => Promise<void>;
  onComplete?: () => Promise<void>;
}

const stripeKey = process.env.REACT_APP_STRIP_KEY || '';
const stripePromise = loadStripe(stripeKey);

const SubscriptionSelectModal: React.FC<SubscriptionSelectModalProps> = ({ open, onPlanSelected, onComplete }) => {
  const { company, createOrUpdateSubscription } = useCompany();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { getPaymentMethods } = useCompany();
  const [paymentMethod, setPaymentMethod] = useState<any>(null);

  useEffect(() => {
    // If company already has a subscription plan but no payment method, show payment form
    if (company?.subscriptionPlan && !paymentMethod) {
      setSelectedPlan(company.subscriptionPlan);
      setShowPaymentForm(true);
      loadPaymentMethods();
    }
  }, [company, open]);

  useEffect(() => {
    if (showPaymentForm) {
      loadPaymentMethods();
    }
  }, [showPaymentForm]);

  const loadPaymentMethods = async () => {
    try {
      const methods = await getPaymentMethods();
      setPaymentMethod(methods?.[0] || null);
      return methods; // Return methods for use in handlePaymentMethodSaved
    } catch (e) {
      console.error('Error fetching payment methods:', e);
      return [];
    }
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
    setShowPaymentForm(true);
  };

  const handlePaymentMethodUpdated = async () => {
    await loadPaymentMethods();
    if (paymentMethod && selectedPlan) {
      await completeSubscription();
    }
  };

  const completeSubscription = async () => {
    if (!selectedPlan) return;
    
    setIsSubmitting(true);
    try {
      // Create or update Stripe subscription
      await createOrUpdateSubscription(selectedPlan);
      
      // Update local plan selection
      if (!company?.subscriptionPlan || company.subscriptionPlan !== selectedPlan) {
        await onPlanSelected(selectedPlan);
      }
      
      enqueueSnackbar('Subscription setup completed successfully', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
      });
      
      if (onComplete) {
        await onComplete();
      }
    } catch (error: any) {
      console.error('Error completing subscription:', error);
      enqueueSnackbar(
        error?.response?.data?.message || 'Failed to complete subscription. Please try again.',
        {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentMethodSaved = async () => {
    try {
      // Reload payment methods to get the newly saved one
      const paymentMethods = await loadPaymentMethods();
      
      // Verify payment method was saved
      if (!paymentMethods || paymentMethods.length === 0) {
        throw new Error('Payment method was not saved. Please try again.');
      }
      
      // Complete subscription - the backend will set the payment method as default
      if (selectedPlan) {
        await completeSubscription();
      }
    } catch (error: any) {
      console.error('Error handling payment method save:', error);
      enqueueSnackbar(
        error?.message || 'Error saving payment method. Please try again.',
        {
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' }
        }
      );
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}} // Prevent closing by clicking outside
      PaperProps={{
        sx: {
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ pb: 3, pt: 3 }}>
        <Typography variant="h5" component="div" align="center" gutterBottom>
          {showPaymentForm ? 'Add Payment Method' : 'Choose Your Subscription Plan'}
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary">
          {showPaymentForm 
            ? 'Please add a payment method to complete your subscription' 
            : 'Select a plan to get started with your account'}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 4, pb: 4, pt: 3, overflow: 'visible' }}>
        {!showPaymentForm ? (
          <>
            <Box 
              sx={{ 
                mb: 4,
                py: 2.5,
                px: 3,
                backgroundColor: 'coolGray.700',
                borderRadius: 1,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'amber.500', mb: 1.5 }}>
                  1 Month Free Trial
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium', color: '#FFFFFF' }}>
                  Start your subscription with a <strong style={{ color: 'amber.500' }}>1-month free trial</strong>. 
                  After the trial period ends, your subscription will begin automatically. Cancel any time.
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={3} sx={{ alignItems: 'stretch', my: 2 }}>
          {SUBSCRIPTION_PLANS.map((plan, index) => (
            <Grid 
              item 
              xs={12} 
              md={4} 
              key={plan.id} 
              sx={{ 
                display: 'flex',
                order: plan.isBestValue 
                  ? { xs: 1, md: 2 } 
                  : plan.id === 'basic' 
                  ? { xs: 3, md: 1 } 
                  : { xs: 2, md: 3 }
              }}
            >
              <Card
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.isBestValue 
                    ? '3px solid' 
                    : selectedPlan === plan.id 
                    ? '2px solid' 
                    : '1px solid',
                  borderColor: plan.isBestValue 
                    ? 'primary.main' 
                    : selectedPlan === plan.id 
                    ? 'primary.main' 
                    : 'divider',
                  position: 'relative',
                  transform: plan.isBestValue ? 'scale(1.05)' : 'scale(0.95)',
                  transformOrigin: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: plan.isBestValue ? 8 : 1,
                  background: plan.isBestValue 
                    ? 'background.paper'
                    : 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                  zIndex: plan.isBestValue ? 2 : 1,
                  '&:hover': {
                    boxShadow: plan.isBestValue ? 12 : 4,
                    transform: plan.isBestValue ? 'scale(1.1)' : 'scale(1.02)',
                  },
                }}
              >
                {plan.isBestValue && (
                  <Chip
                    label="Best Value"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      height: 32,
                      px: 1,
                    }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, pt: plan.isBestValue ? 4 : 2, pb: 1, px: 1.5 }}>
                  <Typography 
                    variant={plan.isBestValue ? "h6" : "body1"} 
                    component="h3" 
                    gutterBottom
                    sx={{ 
                      fontWeight: plan.isBestValue ? 'bold' : 'medium',
                      color: plan.isBestValue ? 'primary.main' : 'text.primary',
                      mb: 0.5,
                      fontSize: plan.isBestValue ? '1.1rem' : '1rem'
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Typography 
                    variant={plan.isBestValue ? "h4" : "h5"} 
                    component="div" 
                    sx={{ 
                      mb: 0.5,
                      fontWeight: plan.isBestValue ? 'bold' : 'normal',
                      color: plan.isBestValue ? 'primary.main' : 'text.primary',
                      fontSize: plan.isBestValue ? '1.75rem' : '1.5rem'
                    }}
                  >
                    ${plan.price}
                    <Typography 
                      component="span" 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ ml: 0.5, fontSize: '0.75rem' }}
                    >
                      /month
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 32, fontSize: '0.85rem' }}>
                    {plan.description}
                  </Typography>
                  <Box sx={{ mt: 0.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.85rem' }}>
                      {plan.openJobs === 'unlimited' ? 'Unlimited' : `${plan.openJobs}`} Open Jobs
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 1, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={selectedPlan === plan.id ? 'outlined' : 'contained'}
                    color="primary"
                    size="small"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: plan.isBestValue ? 'bold' : 'normal',
                      py: 0.75,
                    }}
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={isSubmitting}
                  >
                    {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
          </Grid>
          </>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {selectedPlan && (
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  bgcolor: 'primary.50', 
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  textAlign: 'center',
                  cursor: 'pointer',
                  maxWidth: '600px',
                  width: '100%',
                  '&:hover': {
                    bgcolor: 'primary.100',
                  }
                }}
                onClick={() => {
                  setShowPaymentForm(false);
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Selected Plan:
                </Typography>
                <Typography variant="h6" color="primary.main">
                  {SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.name} - 
                  ${SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.price}/month
                </Typography>
              </Box>
            )}
            <Box sx={{ width: '100%', maxWidth: '600px' }}>
              <Elements stripe={stripePromise}>
                <PaymentForm 
                  paymentMethod={paymentMethod} 
                  methodUpdated={handlePaymentMethodSaved} 
                />
              </Elements>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionSelectModal;

